import { useState } from "react";

import { BasicModal } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import ContactUsHook from "app/pages/sales-intelligence/common-components/ContactUsHook/ContactUsHook";
import { contactUsModalConfig } from "app/pages/sales-intelligence/common-components/ContactUsModal/configs";
import ModalContent from "app/pages/sales-intelligence/common-components/ContactUsModal/modalContent/ModalContent";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import CrmEnrichmentCard from "app/pages/sales-intelligence/modules/enrichment/components/CrmEnrichmentCard/CrmEnrichmentCard";
import useCrmEnrichmentQuota from "app/pages/sales-intelligence/modules/quota/hooks/useCrmEnrichmentQuota";
import { INTEGRATION_IMAGE } from "app/pages/sales-intelligence/pages/management/constants/constants";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { isTraditionalTrialOrAfterTrialUser } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import { requestFeature } from "pro-features/components/Modals/src/HookModal/services/requestFeature";

import EnrichmentCardButton from "../../components/EnrichmentCardButton/EnrichmentCardButton";

import { StyledCrmEnrichmentContainer, StyledCrmEnrichmentContent, StyledCrmEnrichmentTitle } from "./styles";

interface ICrmEnrichmentContainerProps {
    onGainAccessRedirect: VoidFunction;
}

const CrmEnrichmentContainer = ({ onGainAccessRedirect }: ICrmEnrichmentContainerProps) => {
    const [isOpenContactUsModal, setIsOpenContactUsModal] = useState<boolean>(false);

    const { isAvailableHubspotEnrichment, isAvailableSalesforceEnrichment, isLoading } = useCrmEnrichmentQuota();

    const handleRedirectToManagementPage = (integrationName: Integration): void => {
        void enrichmentTrackingService.trackStartCrmEnrichmentAction(integrationName);

        void siNavigator.goToCrmManagementPage({ crm: integrationName });
    };

    if (isTraditionalTrialOrAfterTrialUser(swSettings)) {
        return (
            <Stack>
                <ContactUsHook
                    title={translate("si.enrichment.hook.contact_us.title")}
                    subTitle={translate("si.enrichment.hook.contact_us.subtitle")}
                    onClick={() => {
                        void requestFeature("CRM Enrichment");
                    }}
                />
            </Stack>
        );
    }

    return (
        <StyledCrmEnrichmentContainer>
            <I18n
                translatable={{ key: "si.enrichment.crm.title" }}
                element={StyledCrmEnrichmentTitle}
            />
            <StyledCrmEnrichmentContent>
                <CrmEnrichmentCard
                    isLoading={isLoading}
                    imageUrl={INTEGRATION_IMAGE.salesforce}
                    ctaButton={
                        isAvailableSalesforceEnrichment ? (
                            <EnrichmentCardButton
                                text={translate("si.enrichment.crm.button.primary.text")}
                                onClick={() => handleRedirectToManagementPage("salesforce")}
                                type="primary"
                            />
                        ) : (
                            <EnrichmentCardButton
                                text={translate("explore.our.packages")}
                                onClick={onGainAccessRedirect}
                                type="trial"
                            />
                        )
                    }
                    isCrmAvailable={isAvailableSalesforceEnrichment}
                    videoUrl=""
                    description={translate("si.enrichment.crm.salesforce.text")}
                    isActionDisabled={false}
                    disabledActionText={translate("si.enrichment.crm.contact-admin.text")}
                />
                <CrmEnrichmentCard
                    isLoading={isLoading}
                    imageUrl={INTEGRATION_IMAGE.hubspot}
                    ctaButton={
                        isAvailableHubspotEnrichment ? (
                            <EnrichmentCardButton
                                text={translate("si.enrichment.crm.button.primary.text")}
                                onClick={() => handleRedirectToManagementPage("hubspot")}
                                type="primary"
                            />
                        ) : (
                            <EnrichmentCardButton
                                text={translate("explore.our.packages")}
                                onClick={onGainAccessRedirect}
                                type="trial"
                            />
                        )
                    }
                    isCrmAvailable={isAvailableHubspotEnrichment}
                    videoUrl=""
                    description={translate("si.enrichment.crm.hubspot.text")}
                    isActionDisabled={salesSettingsHelper.isAdmin() ? false : !salesSettingsHelper.hasCrmAccess()}
                    disabledActionText={translate("si.enrichment.crm.contact-admin.text")}
                />
            </StyledCrmEnrichmentContent>
            <BasicModal
                isOpen={isOpenContactUsModal}
                closeIconConfig={{ show: true, onClick: () => setIsOpenContactUsModal(false) }}
                customStyles={contactUsModalConfig}
                onRequestClose={() => setIsOpenContactUsModal(false)}
            >
                <ModalContent
                    title={translate("hook_modal.get_feature.success.title")}
                    subTitle={translate("si.modal.get_feature.success.subtitle.v-2")}
                    onClick={() => setIsOpenContactUsModal(false)}
                    buttonText={translate("si.modal.get_feature.success.btn.close")}
                />
            </BasicModal>
        </StyledCrmEnrichmentContainer>
    );
};

export default CrmEnrichmentContainer;
